import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Filter, X, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BotCard from './BotCard';
import useBotRegistry from '../hooks/useBotRegistry';

const BotSelectionCarousel = ({
  gameType,
  maxBots = 5,
  selectedBots = [],
  onBotsChange,
  disabled = false
}) => {
  const { t } = useTranslation(['bots', 'gameCreation']);
  const {
    bots,
    loading,
    error,
    toggleBot,
    isBotSelected,
    getAvailableDifficulties,
    getAvailableTraits
  } = useBotRegistry(gameType);

  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [traitFilter, setTraitFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Bots still available to add: hide already-selected bots so each
  // bot only ever has one card on the page, then apply the filters
  const availableBots = useMemo(() => {
    return bots.filter(bot => {
      if (selectedBots.some(b => b.id === bot.id)) {
        return false;
      }
      if (difficultyFilter !== 'all' && bot.difficulty !== difficultyFilter) {
        return false;
      }
      if (traitFilter !== 'all' && !bot.personality.includes(traitFilter)) {
        return false;
      }
      return true;
    });
  }, [bots, selectedBots, difficultyFilter, traitFilter]);

  const handleBotToggle = (botId) => {
    if (disabled) return;

    const bot = bots.find(b => b.id === botId);
    if (!bot) return;

    const isCurrentlySelected = selectedBots.some(b => b.id === botId);
    let newSelectedBots;

    if (isCurrentlySelected) {
      // Remove bot
      newSelectedBots = selectedBots.filter(b => b.id !== botId);
    } else {
      // Add bot if not at max capacity
      if (selectedBots.length < maxBots) {
        newSelectedBots = [...selectedBots, bot];
      } else {
        return; // Can't add more bots
      }
    }

    onBotsChange?.(newSelectedBots);
  };

  const clearAllBots = () => {
    if (disabled) return;
    onBotsChange?.([]);
  };

  const availableDifficulties = getAvailableDifficulties();
  const availableTraits = getAvailableTraits();

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-400">Loading bots for {gameType}...</p>
      </div>
    );
  }

  if (error || bots.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Bot className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          {error ? 'Failed to load bots' : 'No bots available'}
        </h3>
        {error && <p className="text-gray-400 text-sm">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t('selection.title', { ns: 'bots' })}
          </h3>
          <p className="text-gray-400 text-sm">
            {t('selection.subtitle', { ns: 'bots' })}
          </p>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filter</span>
        </button>
      </div>

      {/* Selection Summary */}
      <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-400" />
            <span className="text-white font-medium">
              {t('setup.bots.selectedCount', {
                ns: 'gameCreation',
                count: selectedBots.length
              })}
            </span>
          </div>

          <div className="text-sm text-gray-400">
            ({selectedBots.length}/{maxBots} slots used)
          </div>
        </div>

        {selectedBots.length > 0 && (
          <button
            onClick={clearAllBots}
            disabled={disabled}
            className="flex items-center gap-2 px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded text-sm transition-colors disabled:opacity-50"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>

      {/* Max Bots Warning */}
      {selectedBots.length >= maxBots && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-yellow-600/20 border border-yellow-600/30 rounded-lg"
        >
          <AlertCircle className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 text-sm">
            {t('setup.bots.maxReached', { ns: 'gameCreation' })}
          </span>
        </motion.div>
      )}

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Difficulty Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('setup.advanced.difficulty', { ns: 'gameCreation' })}
                </label>
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Difficulties</option>
                  {availableDifficulties.map(difficulty => (
                    <option key={difficulty} value={difficulty}>
                      {t(`selection.difficulty.${difficulty}`, { ns: 'bots' })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Trait Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('selection.personalityTraits', { ns: 'bots' })}
                </label>
                <select
                  value={traitFilter}
                  onChange={(e) => setTraitFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Traits</option>
                  {availableTraits.map(trait => (
                    <option key={trait} value={trait}>
                      {t(`traits.${trait}`, { ns: 'bots' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(difficultyFilter !== 'all' || traitFilter !== 'all') && (
              <div className="text-center">
                <button
                  onClick={() => {
                    setDifficultyFilter('all');
                    setTraitFilter('all');
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Bots Display */}
      {selectedBots.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-medium text-white">Selected Bots</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedBots.map(bot => (
              <BotCard
                key={bot.id}
                bot={bot}
                isSelected={true}
                onDeselect={handleBotToggle}
                disabled={disabled}
                compact={true}
                showDescription={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Bots Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-medium text-white">
            Available Bots
            {availableBots.length < bots.length && (
              <span className="text-sm text-gray-400 ml-2">
                ({availableBots.length} of {bots.length})
              </span>
            )}
          </h4>
        </div>

        {availableBots.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>
              {selectedBots.length === bots.length
                ? 'All bots have been added to your game'
                : 'No bots match the current filters'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableBots.map(bot => {
              const canSelect = selectedBots.length < maxBots;

              return (
                <BotCard
                  key={bot.id}
                  bot={bot}
                  onSelect={canSelect ? handleBotToggle : undefined}
                  disabled={disabled || !canSelect}
                  showDescription={true}
                  compact={false}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BotSelectionCarousel;